import { create } from "zustand";

const emptySelections = { locations: [], systems: [], crops: [], resources: [] };

// Migrate legacy surface tags (where glazed/opaque lived in `category`) to the new model where
// base type and material are independent layers (a surface can be e.g. Exterior + Glazed).
const _BUCKET_TO_BASE = { roof: "roof", base: "ground", N: "exterior", S: "exterior", E: "exterior", W: "exterior" };
function migrateSurfaceTags(tags) {
  return (tags || []).map((t) => {
    if (t && (t.category === "glazed" || t.category === "opaque") && !t.material) {
      return { ...t, material: t.category, category: _BUCKET_TO_BASE[t.autoBucket] || "exterior" };
    }
    return t;
  });
}


// Gross surface area (m²) of a picked location, from the building-layer mesh measurements.
// Used as the "full surface" default for an instance's Allocation Area when the user leaves it open.
export function locationSurfaceArea(grossAreas, id) {
  if (!grossAreas || !id) return 0;
  if (id === "rooftop") return grossAreas.roof || 0;
  if (id === "ground") return grossAreas.base || 0;
  if (id.startsWith("facade_")) return grossAreas.facade?.[id.split("_")[1]?.toUpperCase()] || 0;
  return 0;
}

// Derive per-system allocated area (m²) by AGGREGATING both input types (Section 4 mixed-state):
// manually entered "General Entry" area PLUS the sum of any drafted 3D block areas for that system.
function recomputeAreas(manual, blocks) {
  const out = { ...(manual || {}) };
  blocks.forEach((b) => { if (!b.systemId) return; out[b.systemId] = +(((out[b.systemId] || 0)) + (b.area || 0)).toFixed(1); });
  return out;
}

// P3 — a single Location can host MANY systems ("nested hosting"). All systems placed at a
// location share ONE Allocated Area (defined by the first block placed there). This returns the
// shared cap + how much footprint is already consumed, so callers can hard-block overflow.
function locationAllocation(blocks, locAllocations, locationId, excludeId) {
  const existing = (blocks || []).filter((b) => b.locationId === locationId && b.id !== excludeId);
  const used = existing.reduce((sum, b) => sum + (Number(b.area) || 0), 0);
  // Shared Allocated Area: the persisted per-location zone wins; else the first block's allocatedArea.
  const persisted = Number(locAllocations?.[locationId]) || 0;
  const cap = persisted > 0 ? persisted : (existing.length ? (Number(existing[0].allocatedArea) || 0) : 0);
  return { existing, used, cap, persisted };
}

// Multi-zone (Option A): the capacity identity of a block. A block carries its own zoneId; legacy
// blocks (no zoneId) fall back to their locationId so old scenarios keep working unchanged.
function zoneKeyOf(b) { return (b && (b.zoneId || b.locationId)) || null; }

// Zone-scoped capacity: systems that share a zoneId share ONE Allocated Area. Multiple independent
// zones can coexist on one location — capacity is tracked per zoneId, never merged by location.
function zoneAllocation(blocks, zoneAllocations, locAllocations, zoneId, excludeId) {
  const existing = (blocks || []).filter((b) => zoneKeyOf(b) === zoneId && b.id !== excludeId);
  const used = existing.reduce((sum, b) => sum + (Number(b.area) || 0), 0);
  const persisted = Number(zoneAllocations?.[zoneId] ?? locAllocations?.[zoneId]) || 0;
  const cap = persisted > 0 ? persisted : (existing.length ? (Number(existing[0].allocatedArea) || 0) : 0);
  return { existing, used, cap, persisted };
}

export const useStudio = create((set, get) => ({
  // Seeded knowledge base (crops, systems, waste loops, solar, rainwater)
  kb: null,

  // Onboarding gate — Site & Climate must be set before the studio opens
  siteConfirmed: false,

  // Geo & climate
  geo: null,
  weather: null,
  weatherLoading: false,
  weatherError: null,

  // Omni-directional rings — Location-first default (Hotfix 5.0 rollback)
  rootPillar: "locations",
  // Anchor & Ripple (Hotfix 5.0): the ring the user engaged FIRST becomes the anchor; the
  // other rings are constrained ("rippled") from it. null until the first draft/selection.
  anchorRing: null, // "locations" | "systems" | "crops" | null
  selections: emptySelections,

  // Section 3 — manual "Target Notch" per ring (0..1), client-brief calibration
  targets: {}, // { [pillar]: pct }

  // True North offset (degrees, 0..360) — derived from the .3dm import orientation
  northOffset: 0,

  // Section 6 — Analysis Mode (ring-synced viability heatmaps)
  analysisMode: false,
  season: "SUM", // SPR | SUM | AUT | WNT — drives sun-path / temp recalculation
  dliThreshold: 12, // mol/m²·d — Crops survivability cutoff for the heatmap
  areaThreshold: 40, // m² — Systems allocation strictness for the heatmap
  siteDliOverride: null, // manual site DLI (mol/m²·d) when no climate data — honours zero-fake-math

  // Measure & Section Box tool state (mirrored to the store so the compact/embed shell can drive
  // them from native controls rendered OUTSIDE the 3D canvas).
  measureActive: false,
  measureInfo: null,
  measureClearNonce: 0,
  sectionOn: false,
  sectionXYZ: { x: 1, y: 1, z: 1 },

  // Area allocation (m²) per selected system — drives KPI unlocking
  areas: {}, // { [systemId]: number } — aggregated manual + drafted per system
  manualAreas: {}, // { [systemId]: number } — General-Entry manual m² (Section 4 mixed-state)
  areaFocusSystem: null, // system id awaiting a surface pick from the 3D model

  // Volumetric equipment blocks placed in the 3D model, keyed by resource id
  resourceBlocks: {}, // { [resourceId]: { length, width, height, volume, capacity, unit } }
  placeResource: null, // { id, dims } armed for a volumetric placement click

  // General-Entry auto-visualization: Viewport3D consumes this to auto-drop a block
  // of the given area on the largest upward-facing (roof) surface, then clears it.
  autoPlaceRequest: null, // { systemId, area }

  // Location-ring General Entry: manual m² per location + flat highlighted footprints
  // auto-dropped on a valid surface (roof/ground/facade), mirroring the Systems flow.
  locationAreas: {},        // { [locationId]: number }
  locationBlocks: [],       // [{ id, locationId, locationLabel, area, geom }]
  activeLocationBlockId: null,
  autoPlaceLocationRequest: null, // { locationId, area, _id }

  // Section 7/8 — drafted BIA system blocks (parametric extrusions). Each is a unique
  // instance on a building surface with its own area, height, volume & crop allocations.
  systemBlocks: [], // [{ id, systemId, systemLabel, dbSystems, area, height, volume, allocations:{} }]
  activeBlockId: null,

  // §10/§11 — persistent scenario faces (whole-face capture + parametric footprint).
  // 3D geometry lives in Viewport3D keyed by id; these are the parametric/sidebar values.
  scenarioFaces: [], // [{ id, pillar, tag, totalArea, utilizedArea, offset:{a,b}, systemId, extruded }]
  activeFaceId: null,
  placementRequest: null, // { faceId, systemId } — Viewport3D consumes this to extrude, then clears it

  // 3D model
  model: { status: "empty", name: null, dims: null, error: null },

  // Local .dsymbia serialization — the original uploaded geometry (Base64) so the whole
  // project can be exported to / re-opened from a single desktop file.
  sourceFile: null,       // { name, ext, base64 } | null (sample building has no file)
  pendingRehydrate: false, // set on import; Viewport3D re-draws volumes/faces once the model is ready

  // Building typology & occupancy (Section 1) + derived dynamic opportunity area (Section 5)
  buildingTypology: null,   // typology id
  occupancy: null,          // estimated number of people
  grossAreas: null,         // { roof, base, facade:{total,N,S,E,W} } from building-layer meshes
  opportunity: null,        // backend result: { opportunity_area, notch_pct, breakdown, baselines }
  // Hybrid allocation mode per ring (Section 4): "general" (manual m²) | "draw" (3D drafting)
  allocationMode: { locations: "general", systems: "general", crops: "general", resources: "general" },

  // Phase 2 — 3D interaction tools. Building is INERT until a tool is armed from a ring.
  //   pickTool: null | { kind:'surface' } (Location whole-face) | { kind:'volume', systemId } (tag existing mesh)
  pickTool: null,
  // Draw shape mode for sketching system footprints AND allocation zones on the model.
  draftDrawMode: "rect", // 'rect' (drag) | 'poly' (multi-point click)
  setDraftDrawMode: (draftDrawMode) => set({ draftDrawMode }),
  // Numeric L×W×H override request for a drafted block, consumed by Viewport3D.
  blockResize: null, // { id, length, width, height, nonce }
  // Direct-manipulation gizmo mode for the active system block: 'move' | 'resize' (resize scales X/Y footprint + Z height).
  blockXformMode: "move",

  // PHASE 3 — Required Pairing. Location (canvas) and System (hardware) MUST be paired into a
  // single Instance { locationId, systemId, area }. `pairDraft` is the in-progress pairing being
  // assembled in the active ring tab; committing it creates a systemBlock (the single source of
  // truth, carrying locationId) and advances BOTH rings. origin = which ring started the pair.
  pairDraft: null, // { origin:'locations'|'systems', locationId, systemId, area, height }

  // Remember-last-host — the environment (host) of the most recently picked system, so the
  // system combobox can pin that group to the top when adding another system to the same zone.
  lastSystemHost: null,

  // P3 — capacity-lock rejection surfaced to the UI when a system would overflow a location's
  // single shared Allocated Area. { locationId, message } | null. Cleared on new draft/edit.
  allocError: null,

  // P3 — persistent per-location Allocated Area (m²). Survives across drafts so a system drawn/added
  // later at the same location inherits the SAME zone instead of defaulting to the full surface.
  locationAllocations: {},

  // Multi-zone (Option A) — persistent per-ZONE Allocated Area (m²), keyed by a unique zoneId. A
  // single location can host MANY independent zones, each with its own cap + systems. This is the
  // primary capacity map; locationAllocations remains only as a legacy fallback for old blocks.
  zoneAllocations: {},
  // Optional user-given names per zoneId (overrides the "<Location> · Zone N" auto-label).
  zoneLabels: {},
  // Persisted zone polygon geometry per zoneId {origin:[x,y,z], u, v, n, outline:[[a,b],...]} so a
  // system's MOVE drag can be clamped inside its OWN zone (survives save/reload).
  zoneGeoms: {},

  // PHASE 4 — Mass/Energy Balance tunables. synergyWeights: user-adjustable balance priorities
  // (default equal). resourceInputs: manual anchors + editable literature constants.
  synergyWeights: { water: 0.25, energy: 0.25, carbon: 0.25, spatial: 0.25 },
  resourceInputs: { wasteHeatKw: 0, pvAreaM2: 0, runoffCoeff: 0.8, pvEff: 0.2, pvPR: 0.75, hrvEff: 0.6, divert: {}, locked: {}, thermal: {} },
  // Occupancy input mode: "people" (direct count) or "density" (GFA ÷ m²/person → people).
  occupancyMode: "people",
  gfa: null,        // gross floor area (m²) — user-entered, used only in density mode
  occDensity: null, // m²/person (from ASHRAE 62.1 defaults or user)
  setOccupancyMode: (occupancyMode) => set({ occupancyMode }),
  setGfa: (v) => set({ gfa: v === "" || v == null ? null : Math.max(0, Number(v) || 0) }),
  setOccDensity: (v) => set({ occDensity: v === "" || v == null ? null : Math.max(0, Number(v) || 0) }),
  // Per-resource thermal-exchange config (recovery method, effectiveness, available kW, operating profile).
  setResourceThermal: (id, patch) =>
    set((s) => ({ resourceInputs: { ...s.resourceInputs, thermal: { ...(s.resourceInputs.thermal || {}), [id]: { ...((s.resourceInputs.thermal || {})[id] || {}), ...patch } } } })),

  // PHASE 5 — Energy operational profile (waste-heat + renewable balance across day/night &
  // summer/winter). energyParams holds only the user's edits layered on top of the backend
  // DEFAULTS; energyClimateMonths caches Open-Meteo monthly normals; energyOverride holds a
  // re-uploaded hourly Excel. energyPanel is the floating popout's open/minimized state (transient).
  energyPanel: { source: null, minimized: false },
  energyMonth: new Date().getMonth(),
  energyParams: {},
  energyClimateMonths: null,
  energyOverride: null,

  // Carbon comparative baseline (vs "Current Conventional System") — every field editable & cited.
  // freightEF (GLEC v3.2), lossFraction (FAO 2023), food-miles, reefer uplift, nutrient-density loss.
  carbonBaseline: { foodMilesKm: 1500, freightEF: 0.062, reeferUplift: 0.30, lossFraction: 0.254, nutrientLossPct: 0.10, transportFactorKgPerKg: null, transportBasis: null, transportCity: null, transportMode: null, transportAuto: false, gridFactorKgPerKwh: null, gridProvince: null, gridAuto: false },

  // Data-rigor: per-system structural-load overrides (kg/m²). Lets the user replace a
  // "limited data" peer-reviewed literature estimate with their own verified figure.
  systemWeightOverride: {},

  // Persistence & scenarios
  projectId: null,
  saveStatus: "idle",
  scenarios: [],

  // AI assistant
  assistantSessionId:
    typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `sess-${Date.now()}`,

  // ---- actions ----
  setKb: (kb) => set({ kb }),
  confirmSite: () => set({ siteConfirmed: true }),

  setGeo: (geo) => set((s) => {
    const patch = { geo };
    // Auto-adopt the nearest cited city's province grid factor + transport factor when the
    // pinned site is close enough to a database city (<=500 km). Manual choices are preserved.
    const cities = s.kb?.loc_db?.cities || [];
    const grid = s.kb?.loc_db?.grid || {};
    if (geo && geo.lat != null && geo.lng != null && cities.length) {
      const R = 6371, toR = (d) => (d * Math.PI) / 180;
      let near = null, best = Infinity;
      cities.forEach((c) => {
        if (c.lat == null || c.lng == null) return;
        const dLat = toR(c.lat - geo.lat), dLng = toR(c.lng - geo.lng);
        const a = Math.sin(dLat / 2) ** 2 + Math.cos(toR(geo.lat)) * Math.cos(toR(c.lat)) * Math.sin(dLng / 2) ** 2;
        const dist = 2 * R * Math.asin(Math.sqrt(a));
        if (dist < best) { best = dist; near = c; }
      });
      if (near && best <= 500) {
        const cb = { ...s.carbonBaseline };
        const gf = grid[near.province]?.g_per_kwh?.["2026"];
        if (gf != null && (cb.gridAuto || cb.gridFactorKgPerKwh == null)) {
          cb.gridFactorKgPerKwh = gf / 1000; cb.gridProvince = near.province; cb.gridAuto = true;
        }
        const tf = near.transport_import_kgco2e_per_kg;
        if (tf != null && (cb.transportAuto || cb.transportMode == null)) {
          cb.transportFactorKgPerKg = tf; cb.transportCity = near.city; cb.transportMode = "import"; cb.transportAuto = true;
          cb.transportBasis = `Imported (AZ/CA) · ${near.city} · ${near.dist_import_km ?? "—"} km — ${tf} kg CO₂e/kg (cited, auto from site)`;
        }
        patch.carbonBaseline = cb;
        patch.nearestCity = { city: near.city, province: near.province, distance_km: Math.round(best) };
      }
    }
    return patch;
  }),
  setWeather: (weather) => set({ weather }),
  setWeatherLoading: (weatherLoading) => set({ weatherLoading }),
  setWeatherError: (weatherError) => set({ weatherError }),

  setRoot: (rootPillar) => set({ rootPillar }),
  setAnchorRing: (anchorRing) => set({ anchorRing }),

  // Scale scope selected at 3D import — "building" (only primary building counts) | "neighborhood"
  // (all masses pool). Set at import stage; drives dynamic opportunity aggregation.
  scope: "building",
  setScope: (scope) => set({ scope }),

  setTarget: (pillar, pct) => set((s) => ({ targets: { ...s.targets, [pillar]: pct } })),
  clearTarget: (pillar) => set((s) => { const t = { ...s.targets }; delete t[pillar]; return { targets: t }; }),

  setNorthOffset: (deg) => set({ northOffset: ((deg % 360) + 360) % 360 }),

  toggleAnalysis: () => set((s) => ({ analysisMode: !s.analysisMode })),
  setAnalysisMode: (analysisMode) => set({ analysisMode }),
  setSeason: (season) => set({ season }),
  setDliThreshold: (dliThreshold) => set({ dliThreshold }),
  setAreaThreshold: (areaThreshold) => set({ areaThreshold }),
  setSiteDliOverride: (siteDliOverride) => set({ siteDliOverride }),
  setMeasureActive: (measureActive) => set({ measureActive }),
  setMeasureInfo: (measureInfo) => set({ measureInfo }),
  clearMeasureNow: () => set((s) => ({ measureClearNonce: s.measureClearNonce + 1, measureInfo: null })),
  setSectionOn: (sectionOn) => set({ sectionOn }),
  setSectionXYZ: (v) => set((s) => ({ sectionXYZ: typeof v === "function" ? v(s.sectionXYZ) : v })),

  toggle: (pillar, id) =>
    set((s) => {
      const arr = s.selections[pillar] || [];
      const has = arr.includes(id);
      const next = has ? arr.filter((x) => x !== id) : [...arr, id];
      const patch = { selections: { ...s.selections, [pillar]: next } };
      // Anchor & Ripple: selecting a crop first (no location/system instances yet) anchors Crops.
      if (pillar === "crops" && !has && s.anchorRing == null && (s.systemBlocks || []).length === 0) {
        patch.anchorRing = "crops";
      }
      // prune area when a system is deselected
      if (pillar === "systems" && has) {
        const areas = { ...s.areas };
        delete areas[id];
        patch.areas = areas;
        if (s.areaFocusSystem === id) patch.areaFocusSystem = null;
      }
      // prune volumetric block + lock when a resource is deselected
      if (pillar === "resources" && has) {
        const rb = { ...s.resourceBlocks };
        delete rb[id];
        patch.resourceBlocks = rb;
        if (s.placeResource?.id === id) patch.placeResource = null;
        const locked = { ...(s.resourceInputs.locked || {}) };
        if (locked[id]) { delete locked[id]; patch.resourceInputs = { ...s.resourceInputs, locked }; }
      }
      return patch;
    }),

  setArea: (systemId, m2) =>
    set((s) => {
      const manualAreas = { ...s.manualAreas, [systemId]: m2 };
      return { manualAreas, areas: recomputeAreas(manualAreas, s.systemBlocks) };
    }),
  setAreaFocus: (systemId) => set((s) => ({ areaFocusSystem: systemId, allocDraw: systemId ? null : s.allocDraw })),

  requestAutoPlace: (systemId, area) => set({ autoPlaceRequest: { systemId, area, _id: `${Date.now()}-${Math.random().toString(36).slice(2)}` } }),
  clearAutoPlaceRequest: () => set({ autoPlaceRequest: null }),

  // ---- Location-ring General Entry allocations & footprints ----
  setLocationArea: (locationId, m2) => set((s) => ({ locationAreas: { ...s.locationAreas, [locationId]: m2 } })),
  requestAutoPlaceLocation: (locationId, area) => set({ autoPlaceLocationRequest: { locationId, area, _id: `${Date.now()}-${Math.random().toString(36).slice(2)}` } }),
  clearAutoPlaceLocationRequest: () => set({ autoPlaceLocationRequest: null }),
  addLocationBlock: (block) => set((s) => ({
    locationBlocks: [...s.locationBlocks, block],
    activeLocationBlockId: block.id,
    selections: s.selections.locations.includes(block.locationId) ? s.selections : { ...s.selections, locations: [...s.selections.locations, block.locationId] },
  })),
  removeLocationBlock: (id) => set((s) => ({
    locationBlocks: s.locationBlocks.filter((b) => b.id !== id),
    activeLocationBlockId: s.activeLocationBlockId === id ? null : s.activeLocationBlockId,
  })),
  setActiveLocationBlock: (activeLocationBlockId) => set({ activeLocationBlockId }),

  setPlaceResource: (placeResource) => set({ placeResource }),
  setResourceBlock: (id, block) => set((s) => ({ resourceBlocks: { ...s.resourceBlocks, [id]: block } })),
  clearResourceBlocks: () => set({ resourceBlocks: {}, placeResource: null }),

  // ---- Drafted BIA system blocks (Section 7/8) — also the PHASE 3 paired-instance store ----
  addSystemBlock: (block) =>
    set((s) => {
      let zoneAllocations = s.zoneAllocations;
      // Multi-zone capacity locking, keyed by the block's zoneId (falls back to locationId).
      const zid = zoneKeyOf(block);
      if (zid) {
        const { existing, used, cap: sharedCap, persisted } = zoneAllocation(s.systemBlocks, s.zoneAllocations, s.locationAllocations, zid, block.id);
        const cap = persisted > 0 ? persisted : (existing.length ? sharedCap : (Number(block.allocatedArea) || 0));
        const bArea = Number(block.area) || 0;
        if (cap > 0 && used + bArea > cap + 0.5) {
          const free = Math.max(0, cap - used);
          return {
            allocError: {
              locationId: block.locationId,
              message: `${block.locationLabel || block.locationId} zone is full — ${Math.round(used).toLocaleString()} of ${Math.round(cap).toLocaleString()} m² allocated is used. This ${block.systemLabel || "system"} needs ${Math.round(bArea).toLocaleString()} m² but only ${Math.round(free).toLocaleString()} m² is free. Draw a new zone or shrink the hardware.`,
            },
          };
        }
        // Nested systems inherit the zone's shared Allocated Area; persist it for future draws.
        if (cap > 0) { block = { ...block, allocatedArea: cap }; zoneAllocations = { ...s.zoneAllocations, [zid]: cap }; }
      }
      const systemBlocks = [...s.systemBlocks, block];
      const selections = { ...s.selections };
      if (block.systemId && !selections.systems.includes(block.systemId)) selections.systems = [...selections.systems, block.systemId];
      // Draw / Select-Existing-Volume created inside a pairing carry a locationId → auto-complete
      // the pair: advance the Location ring and clear the in-progress draft.
      if (block.locationId && !selections.locations.includes(block.locationId)) selections.locations = [...selections.locations, block.locationId];
      const done = !!block.locationId; // a completed pairing → disarm the draft/volume tools
      return { systemBlocks, selections, areas: recomputeAreas(s.manualAreas, systemBlocks), zoneAllocations, activeBlockId: block.id, pairDraft: null, allocError: null, anchorRing: done ? null : s.anchorRing, allocDraw: done ? null : s.allocDraw, areaFocusSystem: done ? null : s.areaFocusSystem, pickTool: done ? null : s.pickTool };
    }),
  updateSystemBlock: (id, patch) =>
    set((s) => {
      const systemBlocks = s.systemBlocks.map((b) => (b.id === id ? { ...b, ...patch } : b));
      return { systemBlocks, areas: recomputeAreas(s.manualAreas, systemBlocks) };
    }),
  removeSystemBlock: (id) =>
    set((s) => {
      const systemBlocks = s.systemBlocks.filter((b) => b.id !== id);
      return { systemBlocks, areas: recomputeAreas(s.manualAreas, systemBlocks), activeBlockId: s.activeBlockId === id ? null : s.activeBlockId };
    }),
  setActiveBlock: (activeBlockId) => set({ activeBlockId }),
  // Optimization mode — a BIA-derived draft card is created UNASSIGNED (systemId null). The user
  // defines what system it is from the card; this maps the DB system onto the measured volume and
  // surfaces it in the Systems ring, after which allocation/crops behave like any other instance.
  assignBlockSystem: (id, systemId) =>
    set((s) => {
      const sys = s.kb?.systems?.find((x) => x.id === systemId);
      if (!sys) return {};
      const systemBlocks = s.systemBlocks.map((b) =>
        b.id === id ? { ...b, systemId, systemLabel: sys.label, dbSystems: sys.db_systems || [], fromBia: false } : b);
      const selections = { ...s.selections, systems: s.selections.systems.includes(systemId) ? s.selections.systems : [...s.selections.systems, systemId] };
      return { systemBlocks, selections, areas: recomputeAreas(s.manualAreas, systemBlocks), activeBlockId: id };
    }),
  clearSystemBlocks: () => set((s) => ({ systemBlocks: [], activeBlockId: null, areas: recomputeAreas(s.manualAreas, []) })),
  // Edit a committed instance's "queried database metrics" from its active card. Routes each metric
  // to the correct global slice so the change updates the global count: structural → systemWeightOverride,
  // capex/opex → financialOverrides[blockId], water/energy/carbon → block.metricOverrides.
  setBlockMetric: (blockId, systemId, key, value) =>
    set((s) => {
      const empty = value === "" || value == null;
      const v = empty ? null : Math.max(0, parseFloat(value) || 0);
      if (key === "structural") {
        const swo = { ...s.systemWeightOverride };
        if (empty) delete swo[systemId]; else swo[systemId] = v;
        return { systemWeightOverride: swo };
      }
      if (key === "capex_per_m2" || key === "opex_per_m2_yr") {
        const cur = { ...(s.financialOverrides[blockId] || {}) };
        if (empty) delete cur[key]; else cur[key] = v;
        const fo = { ...s.financialOverrides };
        if (Object.keys(cur).length) fo[blockId] = cur; else delete fo[blockId];
        return { financialOverrides: fo };
      }
      const systemBlocks = s.systemBlocks.map((b) => {
        if (b.id !== blockId) return b;
        const mo = { ...(b.metricOverrides || {}) };
        if (empty) delete mo[key]; else mo[key] = v;
        return { ...b, metricOverrides: Object.keys(mo).length ? mo : null };
      });
      return { systemBlocks };
    }),

  // Clear all selections + drafted geometry belonging to a SINGLE ring (scoped purge).
  // Removes that ring's scenario faces + (for Systems) drafted volumes, empties its
  // selection list & target so the ring's SVG resets to 0%. Other rings are untouched.
  clearRing: (pillar) =>
    set((s) => {
      const patch = {};
      const faces = s.scenarioFaces.filter((f) => f.pillar !== pillar);
      if (faces.length !== s.scenarioFaces.length) {
        patch.scenarioFaces = faces;
        if (s.activeFaceId && !faces.some((f) => f.id === s.activeFaceId)) patch.activeFaceId = null;
      }
      // Drafted volumes are Systems-ring instances; clearing Systems removes them all.
      if (pillar === "systems") {
        patch.systemBlocks = [];
        patch.activeBlockId = null;
        patch.areas = {};
        patch.manualAreas = {};
        patch.areaFocusSystem = null;
      }
      patch.selections = { ...s.selections, [pillar]: [] };
      const targets = { ...s.targets };
      delete targets[pillar];
      patch.targets = targets;
      if (pillar === "locations") { patch.locationBlocks = []; patch.activeLocationBlockId = null; patch.locationAreas = {}; }
      if (pillar === "resources") { patch.resourceBlocks = {}; patch.placeResource = null; }
      if (pillar === s.anchorRing) patch.anchorRing = null;
      return patch;
    }),

  // ---- Local .dsymbia project serialization ----
  setSourceFile: (sourceFile) => set({ sourceFile }),
  clearPendingRehydrate: () => set({ pendingRehydrate: false }),

  // ---- Building typology / occupancy / opportunity (Sections 1 & 5) ----
  setTypology: (buildingTypology) => set({ buildingTypology }),
  setOccupancy: (occupancy) => set({ occupancy }),
  setGrossAreas: (grossAreas) => set({ grossAreas }),
  setOpportunity: (opportunity) => set({ opportunity }),

  // Phase 3 — Dynamic Opportunity inputs: explicit MEP/rooftop-equipment reserve (m²) and the
  // contextual shadow fraction (0–1) subtracted before the climate factor.
  mepReserve: 0,
  shadowPct: 0,
  shadowAuto: false, // when true, contextual shadowing is auto-derived (manual input locked)
  // Real context-shade fraction (0–1) computed by the 3D engine: fraction of the building envelope
  // shaded by imported CONTEXT masses across the site's sun path. null = not yet computed / no context.
  contextShade: null,
  setContextShade: (contextShade) => set({ contextShade }),
  mepAuto: false,    // when true, MEP/Roof reserve = raw roof area × mepRoofPct (manual input locked)
  mepRoofPct: 0.15,  // editable coefficient — share of raw roof reserved for MEP/plant equipment
  setMepReserve: (m2) => set({ mepReserve: Math.max(0, Number(m2) || 0) }),
  setShadowPct: (p) => set({ shadowPct: Math.max(0, Math.min(0.95, Number(p) || 0)) }),
  setShadowAuto: (shadowAuto) => set({ shadowAuto }),
  setMepAuto: (mepAuto) => set({ mepAuto }),
  setMepRoofPct: (p) => set({ mepRoofPct: Math.max(0, Math.min(0.95, Number(p) || 0)) }),

  // Manual surface classification — override the auto roof/ground/façade detection by tagging
  // surfaces (whole face or a drawn rectangle sub-region) as ground/roof/podium/interior/exterior.
  classifyMode: false,
  classifyCategory: "roof",   // active BASE type selection (also "auto" = eraser)
  classifyLayer: "base",      // which layer a click affects: "base" (surface type) | "material"
  classifyMaterial: "glazed", // active material when classifyLayer === "material": "glazed" | "opaque"
  classifySubdivide: false,   // rectangle-draw sub-mode (kept for embed API compatibility)
  classifyDrawMode: "face",   // "face" (whole surface) | "rect" (rectangle) | "poly" (free-form polygon)
  surfaceTags: [],            // [{ id, category(base), material:'glazed'|'opaque'|null, area, autoBucket, kind, geo:{...} }]
  reportMeta: { coverTitle: "", clientName: "", logo: null, includeCosting: true }, // client-ready PDF branding
  setReportMeta: (patch) => set((s) => ({ reportMeta: { ...s.reportMeta, ...patch } })),
  setClassifyMode: (classifyMode) => set({ classifyMode }),
  setClassifyCategory: (classifyCategory) => set({ classifyCategory, classifyLayer: "base" }),
  setClassifyLayer: (classifyLayer) => set({ classifyLayer }),
  setClassifyMaterial: (classifyMaterial) => set({ classifyMaterial, classifyLayer: "material" }),
  setClassifySubdivide: (classifySubdivide) => set({ classifySubdivide: !!classifySubdivide, classifyDrawMode: classifySubdivide ? "rect" : "face" }),
  setClassifyDrawMode: (classifyDrawMode) => set({ classifyDrawMode, classifySubdivide: classifyDrawMode === "rect" }),
  addSurfaceTag: (tag) => set((s) => ({ surfaceTagsPast: [...s.surfaceTagsPast, s.surfaceTags].slice(-40), surfaceTags: [...s.surfaceTags, { id: `sft-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, ...tag }] })),
  updateSurfaceTag: (id, patch) => set((s) => ({ surfaceTagsPast: [...s.surfaceTagsPast, s.surfaceTags].slice(-40), surfaceTags: s.surfaceTags.map((t) => (t.id === id ? { ...t, ...patch } : t)) })),
  removeSurfaceTag: (id) => set((s) => ({ surfaceTagsPast: [...s.surfaceTagsPast, s.surfaceTags].slice(-40), surfaceTags: s.surfaceTags.filter((t) => t.id !== id) })),
  clearSurfaceTags: () => set((s) => ({ surfaceTagsPast: [...s.surfaceTagsPast, s.surfaceTags].slice(-40), surfaceTags: [], selectedSurfaceTag: null })),
  // Classify Undo — one-tap revert of the last classification (add / remove / reshape / clear).
  surfaceTagsPast: [],
  undoSurfaceTags: () => set((s) => (s.surfaceTagsPast.length ? { surfaceTags: s.surfaceTagsPast[s.surfaceTagsPast.length - 1], surfaceTagsPast: s.surfaceTagsPast.slice(0, -1), selectedSurfaceTag: null } : {})),
  selectedSurfaceTag: null, // id of the placed region currently selected for edit/delete
  setSelectedSurfaceTag: (selectedSurfaceTag) => set({ selectedSurfaceTag }),

  // Phase 3 — target-setting pathway per ring: "manual" | "auto".
  targetMode: {},
  setTargetMode: (pillar, mode) => set((s) => ({ targetMode: { ...s.targetMode, [pillar]: mode } })),

  // Phase 3 — per-instance CAPEX/OPEX overrides keyed by block id.
  financialOverrides: {},
  setFinancialOverride: (blockId, patch) =>
    set((s) => ({ financialOverrides: { ...s.financialOverrides, [blockId]: { ...(s.financialOverrides[blockId] || {}), ...patch } } })),
  setAllocationMode: (pillar, mode) => set((s) => ({ allocationMode: { ...s.allocationMode, [pillar]: mode } })),
  setPickTool: (pickTool) => set({ pickTool }),

  // Optimization mode — set when the user tags pre-accounted systems in a "BIA System" layer and
  // confirms they only want to optimize existing systems. Goal dials & location targets become
  // fixed inputs; the workflow focuses on scenario development.
  optimizationMode: false,
  setOptimizationMode: (optimizationMode) => set({ optimizationMode }),

  // 3-tier funnel drafting: when armed, the 3D view draws the ALLOCATION ZONE (a location sub-area
  // the user dedicates) rather than a system volume. { origin } records which ring started it.
  allocDraw: null,
  setAllocDraw: (allocDraw) => set((s) => ({ allocDraw, areaFocusSystem: allocDraw ? null : s.areaFocusSystem, pickTool: allocDraw ? null : s.pickTool })),

  // ZONE-FIRST LOCK (issue #4). When the user draws an allocation zone, it becomes the ACTIVE
  // zone: every system placed afterward is hard-bound to this zone's location + area (never the
  // full surface) and stacks inside it — until the user presses Escape / cancels the draft.
  // { locationId, area, label } — the geometric bounds are held in the 3D layer for clamping.
  activeZone: null,
  setActiveZone: (activeZone) => set({ activeZone }),
  clearActiveZone: () => set({ activeZone: null }),
  // Transient hover — the zone the user is hovering in the "pick existing zone" list. Drives a
  // 3D highlight of that zone's placed systems so users can confirm the surface before nesting.
  hoveredZone: null,
  setHoveredZone: (hoveredZone) => set((s) => (s.hoveredZone === hoveredZone ? {} : { hoveredZone })),
  // Set the drawn/typed Allocated Area on an already-committed instance (reverse-order draw path).
  // P3: since a location's systems share ONE Allocated Area, editing it updates every block at
  // that location so the shared cap stays consistent.
  setBlockAllocated: (id, m2) =>
    set((s) => {
      const v = +Number(m2).toFixed(1);
      const target = s.systemBlocks.find((b) => b.id === id);
      const zid = target ? zoneKeyOf(target) : null;
      return {
        systemBlocks: s.systemBlocks.map((b) => ((b.id === id || (zid && zoneKeyOf(b) === zid)) ? { ...b, allocatedArea: v } : b)),
        zoneAllocations: zid ? { ...s.zoneAllocations, [zid]: v } : s.zoneAllocations,
        allocError: null,
      };
    }),

  // P3 — remember a location's Allocated Area (m²) even before any system is committed there.
  setLocationAllocation: (locationId, m2) =>
    set((s) => (locationId ? { locationAllocations: { ...s.locationAllocations, [locationId]: +Number(m2 || 0).toFixed(1) }, allocError: null } : {})),

  // Multi-zone — persist a specific zone's Allocated Area (m²) by zoneId.
  setZoneAllocation: (zoneId, m2) =>
    set((s) => (zoneId ? { zoneAllocations: { ...s.zoneAllocations, [zoneId]: +Number(m2 || 0).toFixed(1) }, allocError: null } : {})),

  // Persist a zone's polygon geometry (for move-clamping) by zoneId.
  setZoneGeom: (zoneId, geom) =>
    set((s) => (zoneId && geom ? { zoneGeoms: { ...s.zoneGeoms, [zoneId]: geom } } : {})),

  // Rename a zone (custom label shown on its instance card + pickers). Empty string clears it.
  renameZone: (zoneId, label) =>
    set((s) => {
      if (!zoneId) return {};
      const next = { ...s.zoneLabels };
      const v = (label || "").trim();
      if (v) next[zoneId] = v; else delete next[zoneId];
      return { zoneLabels: next };
    }),

  // Delete a whole zone: purge every system inside it + its cap/label, and release it if active.
  deleteZone: (zoneId) =>
    set((s) => {
      if (!zoneId) return {};
      const systemBlocks = s.systemBlocks.filter((b) => (b.zoneId || b.locationId) !== zoneId);
      const zoneAllocations = { ...s.zoneAllocations }; delete zoneAllocations[zoneId];
      const zoneLabels = { ...s.zoneLabels }; delete zoneLabels[zoneId];
      const zoneGeoms = { ...s.zoneGeoms }; delete zoneGeoms[zoneId];
      const activeBlockGone = !systemBlocks.some((b) => b.id === s.activeBlockId);
      return {
        systemBlocks,
        zoneAllocations,
        zoneLabels,
        zoneGeoms,
        areas: recomputeAreas(s.manualAreas, systemBlocks),
        activeBlockId: activeBlockGone ? null : s.activeBlockId,
        activeZone: (s.activeZone && s.activeZone.zoneId === zoneId) ? null : s.activeZone,
        allocError: null,
      };
    }),

  // P3 — "＋ Add system to this allocation": open a fresh pairing pre-anchored to a specific ZONE,
  // inheriting its persisted Allocated Area so the new system stacks into the same independent zone.
  beginStackAt: (zoneId) =>
    set((s) => {
      const host = s.systemBlocks.find((b) => zoneKeyOf(b) === zoneId);
      const locationId = host?.locationId || zoneId;
      const alloc = (Number(s.zoneAllocations?.[zoneId]) || Number(s.locationAllocations?.[locationId]) || Number(host?.allocatedArea)) || null;
      const loc = (s.kb?.locations || []).find((l) => l.id === locationId);
      return {
        pairDraft: { origin: "locations", locationId, zoneId, systemId: null, area: 0, allocatedArea: alloc, height: null, overrides: {} },
        rootPillar: "locations",
        anchorRing: s.anchorRing == null ? "locations" : s.anchorRing,
        activeBlockId: null,
        allocError: null,
        // Re-assert the zone lock so the stacked system stays bound to THIS zone (area + location).
        activeZone: alloc > 0 ? { zoneId, locationId, area: +Number(alloc).toFixed(1), label: loc?.label || locationId } : s.activeZone,
      };
    }),

  // "Pick allocation zone": bind the IN-PROGRESS pairing to an already-drawn zone (by zoneId) so the
  // new system nests inside its remaining free area (the zone's cap wins). Reasserts the zone lock
  // so a subsequently drawn footprint inherits + is clamped to the same allocation.
  pickExistingZone: (zoneId) =>
    set((s) => {
      if (!s.pairDraft || !zoneId) return {};
      const host = s.systemBlocks.find((b) => zoneKeyOf(b) === zoneId);
      const locationId = host?.locationId || zoneId;
      const { cap } = zoneAllocation(s.systemBlocks, s.zoneAllocations, s.locationAllocations, zoneId);
      const loc = (s.kb?.locations || []).find((l) => l.id === locationId);
      const area = cap > 0 ? +Number(cap).toFixed(1) : null;
      return {
        pairDraft: { ...s.pairDraft, locationId, zoneId, allocatedArea: area ?? s.pairDraft.allocatedArea },
        activeZone: area != null ? { zoneId, locationId, area, label: loc?.label || locationId } : s.activeZone,
        allocDraw: null,
        allocError: null,
      };
    }),

  // ---- PHASE 3 Required Pairing ----
  beginPair: (origin, key, id) => set((s) => ({
    pairDraft: { origin, locationId: null, systemId: null, area: 0, allocatedArea: null, height: null, overrides: {}, [key]: id },
    anchorRing: s.anchorRing == null ? (origin === "locations" ? "locations" : "systems") : s.anchorRing,
    allocError: null,
  })),
  setPairField: (patch) => set((s) => ({ pairDraft: s.pairDraft ? { ...s.pairDraft, ...patch } : s.pairDraft, allocError: null })),
  setLastSystemHost: (host) => set({ lastSystemHost: host || null }),

  // Circularity loop weights — USER-EDITABLE assumption (no empirical basis for any split), default
  // equal 25% each. Feeds circularityLoops/circularityScore so the "Resources Recovered" headline
  // reflects the user's own priorities. Persisted with the project.
  circWeights: { water: 0.25, energy: 0.25, nutrient: 0.25, carbon: 0.25 },
  circLockEqual: false, // when true, scoring uses equal weights and the editor is hidden (assumption-free)
  setCircWeight: (key, pct) => set((s) => {
    const next = { ...s.circWeights, [key]: Math.max(0, Math.min(1, (Number(pct) || 0) / 100)) };
    return { circWeights: next };
  }),
  applyCircPreset: (weights) => set({ circWeights: { ...weights } }),
  setCircLockEqual: (locked) => set({ circLockEqual: !!locked }),
  resetCircWeights: () => set({ circWeights: { water: 0.25, energy: 0.25, nutrient: 0.25, carbon: 0.25 } }),
  // When ON, valorization equipment footprint is charged against growing area (displaces yield).
  valorizationAreaCharged: false,
  setValorizationAreaCharged: (v) => set({ valorizationAreaCharged: !!v }),
  // Loop-trend history — one snapshot of each circularity loop's % per committed-system count, so
  // the detail window can show how a loop shifts AS SYSTEMS ARE ADDED. Upsert keyed by system count
  // (latest wins for a given count); values are the real computed pcts, never interpolated.
  loopHistory: [],
  recordLoopSnapshot: (snap) => set((s) => {
    if (!snap || snap.systems == null) return {};
    const rest = s.loopHistory.filter((e) => e.systems !== snap.systems);
    return { loopHistory: [...rest, snap].sort((a, b) => a.systems - b.systems).slice(-24) };
  }),
  clearLoopHistory: () => set({ loopHistory: [] }),
  clearAllocError: () => set({ allocError: null }),
  // "Done"/close purges the transient anchor so the NEXT drafted option starts entirely fresh —
  // no held-over anchor scenario or ripple recommendations bleed across drafts.
  clearPairDraft: () => set({ pairDraft: null, anchorRing: null, allocDraw: null, activeZone: null }),

  // ---- Crop-anchored pairing ----
  // Starting from the Crops ring: pick a crop, choose which SYSTEM will host it, then commit —
  // migrating the crop into that system's instance allocations + the global selections.
  cropDraft: null, // { cropId, systemId }
  beginCropDraft: (cropId) => set((s) => ({ cropDraft: { cropId, systemId: null }, anchorRing: s.anchorRing == null ? "crops" : s.anchorRing })),
  setCropDraftSystem: (systemId) => set((s) => ({ cropDraft: s.cropDraft ? { ...s.cropDraft, systemId } : s.cropDraft })),
  clearCropDraft: () => set({ cropDraft: null }),
  commitCropDraft: () =>
    set((s) => {
      const d = s.cropDraft;
      if (!d || !d.cropId || !d.systemId) return {};
      const selections = { ...s.selections };
      if (!selections.crops.includes(d.cropId)) selections.crops = [...selections.crops, d.cropId];
      if (!selections.systems.includes(d.systemId)) selections.systems = [...selections.systems, d.systemId];
      // Migrate the crop into every existing instance of the chosen system (0% share to start).
      const systemBlocks = s.systemBlocks.map((b) =>
        b.systemId === d.systemId && b.allocations[d.cropId] == null
          ? { ...b, allocations: { ...b.allocations, [d.cropId]: 0 } } : b
      );
      return { selections, systemBlocks, cropDraft: null, anchorRing: null };
    }),

  // ---- Draft-session guard (Phase 2) ----
  // Only ONE draft (a system/zone pairing OR a crop attachment) may be active at a time. When a
  // draft is live and the user clicks a DIFFERENT new Zone / System / Crop, we stash the intended
  // action here and prompt the user: "Close active draft" (park it) vs "Keep editing".
  pendingDraftAction: null, // { type:'pair'|'crop', origin?, key?, id } | null
  requestBeginPair: (origin, key, id) => {
    const s = get();
    if (s.pairDraft || s.cropDraft) { set({ pendingDraftAction: { type: "pair", origin, key, id } }); return; }
    get().beginPair(origin, key, id);
  },
  requestBeginCropDraft: (cropId) => {
    const s = get();
    if (s.pairDraft || s.cropDraft) { set({ pendingDraftAction: { type: "crop", id: cropId } }); return; }
    get().beginCropDraft(cropId);
  },
  keepEditingDraft: () => set({ pendingDraftAction: null }),
  // "Close active draft": park an in-progress pairing into Paused Drafts (recoverable) — or clear an
  // empty pairing — drop any crop draft, then open the pending selection so the new draft starts fresh.
  closeActiveAndProceed: () => {
    const p = get().pendingDraftAction;
    if (get().pairDraft) get().pauseDraft();
    if (get().cropDraft) set({ cropDraft: null, anchorRing: null });
    set({ pendingDraftAction: null });
    if (!p) return;
    if (p.type === "pair") get().beginPair(p.origin, p.key, p.id);
    else get().beginCropDraft(p.id);
  },


  // Phase 2 — paused (incomplete) drafts. "Done" saves progress WITHOUT forced validation:
  // a complete draft commits to a systemBlock; an incomplete one parks here as a collapsed
  // summary the user can reopen later. Purely a UI staging area — never feeds KPI math.
  pausedDrafts: [],
  pauseDraft: () =>
    set((s) => {
      const d = s.pairDraft;
      if (!d || (!d.locationId && !d.systemId)) return { pairDraft: null, anchorRing: null, allocDraw: null, activeZone: null };
      const id = d._id || ((typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `draft-${Date.now()}`);
      const entry = { ...d, _id: id };
      const rest = s.pausedDrafts.filter((p) => p._id !== id);
      return { pausedDrafts: [...rest, entry], pairDraft: null, anchorRing: null, allocDraw: null, activeZone: null };
    }),
  resumeDraft: (id) =>
    set((s) => {
      const entry = s.pausedDrafts.find((p) => p._id === id);
      if (!entry) return {};
      return { pairDraft: { ...entry }, pausedDrafts: s.pausedDrafts.filter((p) => p._id !== id) };
    }),
  removePausedDraft: (id) => set((s) => ({ pausedDrafts: s.pausedDrafts.filter((p) => p._id !== id) })),

  // ---- PHASE 4 balance tunables ----
  setSynergyWeight: (key, value) => set((s) => ({ synergyWeights: { ...s.synergyWeights, [key]: Math.max(0, value) } })),
  setResourceInput: (key, value) => set((s) => ({ resourceInputs: { ...s.resourceInputs, [key]: value } })),
  setResourceDivert: (id, pct) => set((s) => ({ resourceInputs: { ...s.resourceInputs, divert: { ...s.resourceInputs.divert, [id]: Math.max(0, Math.min(100, Number(pct) || 0)) } } })),
  // Lock/commit a configured resource loop: only LOCKED loops feed the circularity balance & ring.
  setResourceLocked: (id, val) =>
    set((s) => {
      const locked = { ...(s.resourceInputs.locked || {}) };
      if (val) locked[id] = true; else delete locked[id];
      return { resourceInputs: { ...s.resourceInputs, locked } };
    }),

  // Picked local supplier per system (from the Local Suppliers panel). Lets the user commit a
  // sourcing choice and move on; persisted with the project.
  pickedSuppliers: {},
  setPickedSupplier: (systemId, supplier) => set((s) => ({ pickedSuppliers: { ...s.pickedSuppliers, [systemId]: supplier } })),
  clearPickedSupplier: (systemId) => set((s) => { const n = { ...s.pickedSuppliers }; delete n[systemId]; return { pickedSuppliers: n }; }),

  // Sidebar collapse signal — incrementing it tells every CollapsibleGroup to collapse (used when
  // finishing the Quick Tour to return the user to a clean slate).
  sidebarCollapseSignal: 0,
  collapseSidebar: () => set((s) => ({ sidebarCollapseSignal: s.sidebarCollapseSignal + 1 })),

  // Paused-drafts highlight signal — the gate-header "N paused" badge increments this to scroll the
  // Paused Drafts list into view and briefly flash it.
  pausedHighlight: 0,
  flashPausedDrafts: () => set((s) => ({ pausedHighlight: s.pausedHighlight + 1 })),

  // ---- PHASE 5 energy operational-profile actions ----
  openEnergyPanel: (source) => set({ energyPanel: { source, minimized: false } }),
  closeEnergyPanel: () => set((s) => ({ energyPanel: { ...s.energyPanel, source: null } })),
  minimizeEnergyPanel: (minimized) => set((s) => ({ energyPanel: { ...s.energyPanel, minimized } })),
  setEnergyMonth: (energyMonth) => set({ energyMonth }),
  setEnergyClimateMonths: (energyClimateMonths) => set({ energyClimateMonths }),
  setEnergyOverride: (energyOverride) => set({ energyOverride }),
  clearEnergyOverride: () => set({ energyOverride: null }),
  // Deep-set a param by dotted path, e.g. "building.u_value" or "sources.pv.area_m2".
  setEnergyParam: (path, value) => set((s) => {
    const next = JSON.parse(JSON.stringify(s.energyParams || {}));
    const keys = path.split(".");
    let node = next;
    for (let i = 0; i < keys.length - 1; i++) { node[keys[i]] = node[keys[i]] || {}; node = node[keys[i]]; }
    node[keys[keys.length - 1]] = value;
    return { energyParams: next };
  }),
  resetEnergyParams: () => set({ energyParams: {}, energyOverride: null }),
  setCarbonBaseline: (key, value) => set((s) => ({ carbonBaseline: { ...s.carbonBaseline, [key]: value } })),
  setSystemWeight: (systemId, value) =>
    set((s) => ({ systemWeightOverride: { ...s.systemWeightOverride, [systemId]: value } })),
  // Commit a General-Entry pairing (no 3D) → creates a geometry-less instance carrying both ids.
  commitPair: () =>
    set((s) => {
      const d = s.pairDraft;
      if (!d || !d.locationId || !d.systemId || !(d.area > 0)) return {};
      const sys = (s.kb?.systems || []).find((x) => x.id === d.systemId);
      const loc = (s.kb?.locations || []).find((x) => x.id === d.locationId);
      const id = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `pair-${Date.now()}`;
      // Split the draft's instance overrides: CAPEX/OPEX flow to financialOverrides (keyed by
      // block id), the physical baselines (water/energy/carbon/structural) stay on the block.
      const FIN_KEYS = ["capex_per_m2", "opex_per_m2_yr"];
      const baseOv = {}, finOv = {};
      Object.entries(d.overrides || {}).forEach(([k, v]) => {
        if (v == null || v === "") return;
        (FIN_KEYS.includes(k) ? finOv : baseOv)[k] = v;
      });
      // Proprietary systems (e.g. QT POD) enforce their hardcoded footprint & height regardless
      // of the numeric area the user typed — the physical unit is fixed.
      const ff = sys?.fixed_footprint;
      const footprint = ff ? { length: ff.length, width: ff.width } : null;
      const area = ff ? +(ff.length * ff.width).toFixed(1) : +Number(d.area).toFixed(1);
      const height = ff?.height || d.height || sys?.default_height || 1;
      // 3-tier funnel: the user-zoned Allocated Area for THIS instance. Default when left open is
      // origin-aware — a LOCATION-anchored draft accounts for the full picked-location surface, while
      // a SYSTEM-anchored draft simply matches its own footprint. Falls back to footprint if unknown.
      const surf = locationSurfaceArea(s.grossAreas, d.locationId);
      // ZONE identity (multi-zone): an explicitly picked/stacked zone (d.zoneId) wins; else an active
      // zone bound to this location; else this instance opens its OWN new independent zone (id).
      const activeZoneMatch = (s.activeZone && s.activeZone.locationId === d.locationId) ? s.activeZone : null;
      const zoneId = d.zoneId || activeZoneMatch?.zoneId || id;
      const zoneArea = d.zoneId ? (Number(s.zoneAllocations?.[d.zoneId]) || 0)
        : (activeZoneMatch ? +Number(activeZoneMatch.area).toFixed(1) : 0);
      const openDefault = zoneArea > 0 ? zoneArea
        : (d.origin === "locations" ? (surf > 0 ? +surf.toFixed(1) : area) : area);
      const draftAllocated = zoneArea > 0 ? zoneArea
        : (Number(d.allocatedArea) > 0 ? +Number(d.allocatedArea).toFixed(1) : openDefault);
      // Capacity locking per ZONE: systems sharing a zoneId share ONE Allocated Area. Hard-block a
      // new system whose footprint would push the combined footprints past that zone's cap.
      const { existing: zoneBlocks, used: usedFootprint, cap: sharedCap, persisted } = zoneAllocation(s.systemBlocks, s.zoneAllocations, s.locationAllocations, zoneId, id);
      const cap = zoneArea > 0 ? zoneArea
        : (persisted > 0 ? persisted : (zoneBlocks.length ? (sharedCap || openDefault) : draftAllocated));
      if (cap > 0 && usedFootprint + area > cap + 0.5) {
        const free = Math.max(0, cap - usedFootprint);
        return {
          allocError: {
            locationId: d.locationId,
            message: `${loc?.label || d.locationId} zone is full — ${Math.round(usedFootprint).toLocaleString()} of ${Math.round(cap).toLocaleString()} m² allocated is used. “${sys?.label || d.systemId}” needs ${Math.round(area).toLocaleString()} m² but only ${Math.round(free).toLocaleString()} m² is free. Draw a new zone or shrink the hardware.`,
          },
        };
      }
      // Systems sharing a zoneId inherit its shared Allocated Area; the first block/zone defines it.
      const allocatedArea = cap;
      const block = {
        id, zoneId, systemId: d.systemId, systemLabel: sys?.label || d.systemId, dbSystems: sys?.db_systems || [],
        locationId: d.locationId, locationLabel: loc?.label || d.locationId, ringId: "systems",
        area, allocatedArea, height: +Number(height).toFixed(2), volume: +(area * Number(height)).toFixed(2),
        allocations: {}, geom: null, footprint, rotation: 0, areaMode: "general",
        overrides: null,
        metricOverrides: Object.keys(baseOv).length ? baseOv : null,
      };
      const systemBlocks = [...s.systemBlocks, block];
      const selections = { ...s.selections };
      if (!selections.systems.includes(d.systemId)) selections.systems = [...selections.systems, d.systemId];
      if (!selections.locations.includes(d.locationId)) selections.locations = [...selections.locations, d.locationId];
      const patch = { systemBlocks, selections, areas: recomputeAreas(s.manualAreas, systemBlocks), activeBlockId: id, pairDraft: null, anchorRing: null, allocError: null, zoneAllocations: { ...s.zoneAllocations, [zoneId]: cap } };
      if (Object.keys(finOv).length) patch.financialOverrides = { ...s.financialOverrides, [id]: finOv };
      // Draft-level "queried metrics" push to the global model on Done: the edited structural load
      // updates the system-wide weight override that drives the structural roll-up.
      if (baseOv.structural != null) patch.systemWeightOverride = { ...s.systemWeightOverride, [d.systemId]: baseOv.structural };
      return patch;
    }),
  requestBlockResize: (payload) => set({ blockResize: { ...payload, nonce: Date.now() } }),
  // Hotfix 5.0 §3 — rotate a drafted volume (e.g. the QT POD) 90° in-plane on the site.
  requestBlockRotate: (payload) => set({ blockRotate: { ...payload, nonce: Date.now() } }),
  setBlockXformMode: (blockXformMode) => set({ blockXformMode }),

  // Bundle the full studio state + original geometry into one plain object for download.
  serializeProject: () => {
    const s = get();
    return {
      format: "rcengine",
      version: 1,
      savedAt: new Date().toISOString(),
      projectName: s.geo?.name || "Resource Circularity Project",
      state: {
        geo: s.geo,
        weather: s.weather,
        targets: s.targets,
        northOffset: s.northOffset,
        selections: s.selections,
        areas: s.areas,
        manualAreas: s.manualAreas,
        systemBlocks: s.systemBlocks,
        scenarioFaces: s.scenarioFaces,
        resourceBlocks: s.resourceBlocks,
        locationAreas: s.locationAreas,
        locationBlocks: s.locationBlocks,
        locationAllocations: s.locationAllocations,
        zoneAllocations: s.zoneAllocations,
        zoneLabels: s.zoneLabels,
        zoneGeoms: s.zoneGeoms,
        activeBlockId: s.activeBlockId,
        activeFaceId: s.activeFaceId,
        buildingTypology: s.buildingTypology,
        occupancy: s.occupancy,
        surfaceTags: s.surfaceTags,
        energyParams: s.energyParams,
        energyMonth: s.energyMonth,
        energyOverride: s.energyOverride,
        energyClimateMonths: s.energyClimateMonths,
        pickedSuppliers: s.pickedSuppliers,
        circWeights: s.circWeights,
        circLockEqual: s.circLockEqual,
      },
      model: s.sourceFile, // { name, ext, base64 } | null
    };
  },

  // Push a parsed .dsymbia payload back into the store; flags a rehydration so the 3D
  // engine re-extrudes the saved volumes/faces once the geometry finishes loading.
  rehydrateProject: (data) =>
    set((s) => {
      const st = data.state || {};
      return {
        geo: st.geo ?? s.geo,
        weather: st.weather ?? s.weather,
        targets: st.targets || {},
        northOffset: st.northOffset || 0,
        selections: { ...emptySelections, ...(st.selections || {}) },
        areas: st.areas || {},
        manualAreas: st.manualAreas || {},
        systemBlocks: st.systemBlocks || [],
        scenarioFaces: st.scenarioFaces || [],
        resourceBlocks: st.resourceBlocks || {},
        locationAreas: st.locationAreas || {},
        locationBlocks: st.locationBlocks || [],
        locationAllocations: st.locationAllocations || {},
        zoneAllocations: st.zoneAllocations || {},
        zoneLabels: st.zoneLabels || {},
        zoneGeoms: st.zoneGeoms || {},
        activeLocationBlockId: null,
        activeBlockId: st.activeBlockId || null,
        activeFaceId: st.activeFaceId || null,
        buildingTypology: st.buildingTypology || null,
        occupancy: st.occupancy || null,
        surfaceTags: migrateSurfaceTags(st.surfaceTags || []),
        energyParams: st.energyParams || {},
        energyMonth: st.energyMonth ?? new Date().getMonth(),
        energyOverride: st.energyOverride || null,
        energyClimateMonths: st.energyClimateMonths || null,
        pickedSuppliers: st.pickedSuppliers || {},
        sourceFile: data.model || null,
        pendingRehydrate: true,
        areaFocusSystem: null,
        placeResource: null,
        loopHistory: [],
        circWeights: st.circWeights || { water: 0.25, energy: 0.25, nutrient: 0.25, carbon: 0.25 },
        circLockEqual: !!st.circLockEqual,
      };
    }),

  // ---- §10/§11 scenario faces ----
  addScenarioFace: (face) => set((s) => ({ scenarioFaces: [...s.scenarioFaces, face], activeFaceId: face.id, rootPillar: face.pillar || s.rootPillar })),
  updateScenarioFace: (id, patch) => set((s) => ({ scenarioFaces: s.scenarioFaces.map((f) => (f.id === id ? { ...f, ...patch } : f)) })),
  removeScenarioFace: (id) => set((s) => ({ scenarioFaces: s.scenarioFaces.filter((f) => f.id !== id), activeFaceId: s.activeFaceId === id ? null : s.activeFaceId })),
  setActiveFace: (activeFaceId) => set({ activeFaceId }),
  clearScenarioFaces: () => set({ scenarioFaces: [], activeFaceId: null }),
  requestPlacement: (faceId, systemId) => set({ placementRequest: { faceId, systemId } }),
  clearPlacementRequest: () => set({ placementRequest: null }),

  // ---- §12 Reset loop: purge the DESIGN, keep the SITE (geo/climate/north/mesh) ----
  startNewScenario: () => set((s) => ({
    scenarioFaces: [], activeFaceId: null, placementRequest: null,
    systemBlocks: [], activeBlockId: null,
    locationAreas: {}, locationBlocks: [], activeLocationBlockId: null, autoPlaceLocationRequest: null,
    locationAllocations: {},
    zoneAllocations: {},
    zoneLabels: {},
    zoneGeoms: {},
    activeZone: null,
    selections: { locations: [], systems: [], crops: [], resources: [] },
    areas: {}, manualAreas: {}, targets: {}, areaFocusSystem: null,
    resourceBlocks: {}, placeResource: null,
    rootPillar: "locations",
    anchorRing: null,
    loopHistory: [],
    // kept: geo, weather, northOffset, model, kb, siteConfirmed
  })),

  // Per-block crop allocation (Section 9). Shares are % of the block area (0..100).
  // PHASE-4 sync: assigning/allocating a crop inside a system instance also promotes it to the
  // global selections.crops so the Crops ring reflects it (no double entry). Removing it retracts
  // from selections.crops only when no other instance still uses that crop.
  setBlockAllocation: (id, cropId, pct) =>
    set((s) => {
      const systemBlocks = s.systemBlocks.map((b) =>
        b.id === id ? { ...b, allocations: { ...b.allocations, [cropId]: Math.max(0, pct) } } : b
      );
      const selections = s.selections.crops.includes(cropId)
        ? s.selections : { ...s.selections, crops: [...s.selections.crops, cropId] };
      return { systemBlocks, selections };
    }),
  addBlockCrop: (id, cropId) =>
    set((s) => {
      const systemBlocks = s.systemBlocks.map((b) =>
        b.id === id && b.allocations[cropId] == null ? { ...b, allocations: { ...b.allocations, [cropId]: 0 } } : b
      );
      const selections = s.selections.crops.includes(cropId)
        ? s.selections : { ...s.selections, crops: [...s.selections.crops, cropId] };
      return { systemBlocks, selections };
    }),
  removeBlockCrop: (id, cropId) =>
    set((s) => {
      const systemBlocks = s.systemBlocks.map((b) => {
        if (b.id !== id) return b;
        const allocations = { ...b.allocations };
        delete allocations[cropId];
        return { ...b, allocations };
      });
      const stillUsed = systemBlocks.some((b) => b.allocations[cropId] != null);
      const selections = stillUsed ? s.selections : { ...s.selections, crops: s.selections.crops.filter((c) => c !== cropId) };
      return { systemBlocks, selections };
    }),
  resetBlockShares: (id) =>
    set((s) => ({
      systemBlocks: s.systemBlocks.map((b) =>
        b.id === id ? { ...b, allocations: Object.fromEntries(Object.keys(b.allocations).map((k) => [k, 0])) } : b
      ),
    })),

  // Bi-directional: clicking a surface in the 3D viewport activates the Location
  // ring and selects the tagged location (rooftop / facade_*), which auto-filters systems.
  activateLocation: (id) =>
    set((s) => ({
      rootPillar: "locations",
      selections: s.selections.locations.includes(id)
        ? s.selections
        : { ...s.selections, locations: [...s.selections.locations, id] },
    })),

  setModel: (patch) => set((s) => ({ model: { ...s.model, ...patch } })),
  clearModel: () => set({ model: { status: "empty", name: null, dims: null, error: null } }),

  setProjectId: (projectId) => set({ projectId }),
  setSaveStatus: (saveStatus) => set({ saveStatus }),
  setScenarios: (scenarios) => set({ scenarios }),
  addScenario: (scenario) => set((s) => ({ scenarios: [scenario, ...s.scenarios] })),
  removeScenario: (id) => set((s) => ({ scenarios: s.scenarios.filter((x) => x.id !== id) })),

  // Merge AI-suggested picks into the current selections (never removes existing)
  applySuggestions: ({ crops = [], resources = [], systems = [] }) =>
    set((s) => {
      const merge = (arr, ids) => [...new Set([...arr, ...ids.filter(Boolean)])];
      return {
        selections: {
          ...s.selections,
          systems: merge(s.selections.systems, systems),
          crops: merge(s.selections.crops, crops),
          resources: merge(s.selections.resources, resources),
        },
      };
    }),

  // Restore a saved scenario back into the live studio (overwrites current config)
  loadScenario: (sc) =>
    set(() => {
      const sel = sc.selections || {};
      const ob = sc.onboarding || {};
      const geo = sc.geo || (ob.location ? { name: ob.location } : null);
      // Rebuild per-location Allocated Areas from the saved blocks so capacity locking stays intact.
      const _locAlloc = {};
      (sc.system_blocks || []).forEach((b) => { if (b.locationId && _locAlloc[b.locationId] == null && Number(b.allocatedArea) > 0) _locAlloc[b.locationId] = Number(b.allocatedArea); });
      // Rebuild per-zone Allocated Areas (multi-zone) keyed by zoneId (falls back to locationId).
      const _zoneAlloc = {};
      (sc.system_blocks || []).forEach((b) => { const zid = b.zoneId || b.locationId; if (zid && _zoneAlloc[zid] == null && Number(b.allocatedArea) > 0) _zoneAlloc[zid] = Number(b.allocatedArea); });
      const weather =
        sc.weather ||
        (ob.dli != null || ob.temperature != null
          ? { dli: ob.dli, temperature: ob.temperature, humidity: ob.humidity }
          : null);
      return {
        selections: {
          locations: sel.locations || [],
          systems: sel.systems || [],
          crops: sel.crops || [],
          resources: sel.resources || [],
        },
        areas: sc.areas || {},
        manualAreas: {},
        targets: sc.targets || {},
        northOffset: sc.north_offset || 0,
        geo,
        weather,
        projectId: sc.id,
        areaFocusSystem: null,
        resourceBlocks: {},
        placeResource: null,
        systemBlocks: sc.system_blocks || [],
        locationAllocations: _locAlloc,
        zoneAllocations: _zoneAlloc,
        zoneLabels: {},
        zoneGeoms: {},
        activeBlockId: null,
        circWeights: sc.circ_weights && Object.keys(sc.circ_weights).length
          ? sc.circ_weights : { water: 0.25, energy: 0.25, nutrient: 0.25, carbon: 0.25 },
        circLockEqual: !!sc.circ_lock_equal,
      };
    }),
}));
